import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useHistory, useLocation } from 'react-router-dom'
import { css } from '@emotion/core'
import { SelfCheckinHotelInfo } from '@/components/organisms/self-checkin/hotel-info'
import * as api from '@/apis/aipass'
import { SelfCheckinBasicInfoPluginType } from '@/models/self-checkin/results'
import { useSelfCheckInState } from '@/hooks/use-self-check-in-state'
import { SelfCheckinLayout } from '@/components/layouts/self-checkin-layout'
import { reloadIfVersionHasChanged } from '@/utils/force-version-up'
import { signout } from '@/apis/auth0'
import { useSelfCheckOutState } from '@/hooks/use-self-check-out-state'
import { serialNumberStorageKey } from '@/constants/self-checkin-device-storage'
import { TPlugin } from '@/models/plugin'
import { useHiddenTapGesture } from '@/hooks/use-hidden-tap-gesture'
import { DeviceSettingsModal } from '@/components/organisms/self-checkin/device-settings-modal'
import { createSelfCheckinResponsiveStyle } from '@/utils/front-tablet-responsive-style'

export const FrontTabletHome: React.FC<{}> = () => {
  const { i18n } = useTranslation()
  const { search } = useLocation()
  const history = useHistory()
  const params = new URLSearchParams(search)
  const hotelId = params.get('hotelId') || sessionStorage.getItem('selfci.hotelId')
  const [basicInfo, setBasicInfo] = useState<SelfCheckinBasicInfoPluginType['basicInfo']>()
  const [plugins, setPlugins] = useState<TPlugin[]>([])
  const [tabletCheckoutEnabled, setTabletCheckoutEnabled] = useState<boolean>(false)
  const [showDeviceSettings, setShowDeviceSettings] = useState(false)
  const checkinState = useSelfCheckInState()
  const checkoutState = useSelfCheckOutState()

  const isEnabledIssueKey = (localStorage.getItem(serialNumberStorageKey) || '').length > 0

  const { onTap } = useHiddenTapGesture({
    onTrigger: useCallback(() => setShowDeviceSettings(true), []),
  })

  const loadAndToNextPage = async (nexPage: string): Promise<void> => {
    await api.fetchFacilityBasicInfoInSelfCheckin(hotelId || '')
    history.push(nexPage)
  }

  const backgroundStyle = useMemo(() => {
    if (basicInfo?.extImageId) {
      return css({
        background: `url(${basicInfo.extImageId}) center/cover no-repeat`,
        position: 'relative',
        '&::after': {
          content: '""',
          display: 'block',
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'rgba(0, 0, 0, 0.36)',
          zIndex: 1,
        },
      })
    } else {
      return css({
        background: '#185AA5 0% 0% no-repeat padding-box',
      })
    }
  }, [basicInfo])

  useEffect(() => {
    if (!hotelId) {
      signout()
      history.push({ pathname: '/dashboard' })
      return
    }
    sessionStorage.setItem('selfci.hotelId', hotelId)
    api.fetchFacilityBasicInfoInSelfCheckin(hotelId).then(({ results }) => {
      setBasicInfo(results.basicInfo[0])
      setPlugins(results.plugin)
      reloadIfVersionHasChanged()
    })
    api.fetchTabletCheckoutEnabled(hotelId).then(({ tabletCheckoutEnabled }) => {
      setTabletCheckoutEnabled(tabletCheckoutEnabled)
    })
  }, [hotelId])

  useEffect(() => {
    checkinState.clearState(hotelId || '')
    checkoutState.clearState(hotelId || '')
    window.history.pushState(null, '', window.location.href)
  }, [])

  useEffect(() => {
    i18n.changeLanguage('ja')
  }, [])

  if (!hotelId) {
    return <></>
  }

  return (
    <SelfCheckinLayout
      fullscreen
      header={{ title: basicInfo ? (i18n.language === 'ja' ? basicInfo.hotelName : basicInfo.hotelNameLatin) : '' }}
    >
      <div css={[containerStyle, { fontFamily: 'Noto Sans JP' }]}>
        <div css={[backgroundStyle, { minHeight: '100vh' }]}>
          <div css={menuContainerStyle}>
            <div className="menu-button" onClick={() => loadAndToNextPage('/self-checkin/search-reservation')}>
              <Trans i18nKey="SelfCheckIn.CheckIn" />
            </div>
            {tabletCheckoutEnabled && (
              <div
                className="menu-button"
                onClick={() => loadAndToNextPage(isEnabledIssueKey ? '/self-checkout/read-room-key' : '/self-checkout/search-reservation')}
              >
                <Trans i18nKey="SelfCheckIn.CheckOut" />
              </div>
            )}
          </div>
          <div css={poweredByContainer}>
            {basicInfo && (
              <SelfCheckinHotelInfo
                logoImage={basicInfo.logoImageId}
                telephone={basicInfo.telephone}
                frontOpenTime={basicInfo.frontOpenTime}
                frontCloseTime={basicInfo.frontCloseTime}
                usedIn="top"
                position="static"
              />
            )}
          </div>
        </div>
      </div>
      {/* 左下の隠しタップゾーン: 10回連続タップでデバイス設定モーダルを開く */}
      <div css={hiddenTapZoneStyle} onClick={onTap} />
      {showDeviceSettings && (
        <DeviceSettingsModal
          hotelId={hotelId || ''}
          plugins={plugins}
          isOpen={showDeviceSettings}
          onClose={() => setShowDeviceSettings(false)}
        />
      )}
    </SelfCheckinLayout>
  )
}

const containerStyle = css({
  width: '100%',
  minHeight: '100vh',
})

const menuContainerStyle = css(
  {
    position: 'relative',
    zIndex: 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 64,
    height: '100vh',

    '.menu-button': {
      width: 456,
      maxWidth: 456,
      height: 181,
      flex: '1 1 456px',

      color: '#FFF',
      fontSize: 36,
      letterSpacing: 2.4,
      fontWeight: 'normal',
      lineHeight: 1.5,
      wordBreak: 'keep-all',
      overflowWrap: 'break-word',

      display: 'flex',
      alignItems: 'center',
      textAlign: 'center',
      justifyContent: 'center',

      borderRadius: 10,
      border: '2px solid #fff',
      boxShadow: '0px 3px 10px #00000014',
    },
  },
  createSelfCheckinResponsiveStyle({
    portrait: {
      gap: 40,
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0 40px',
      '.menu-button': {
        width: 350,
        maxWidth: 350,
        height: 181,
        flexBasis: 350,
        fontSize: 36,
      },
    },
    landscape13: {
      gap: 60,
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0 108px',
      '.menu-button': {
        width: 509,
        maxWidth: 509,
        height: 215,
        flexBasis: 509,
        fontSize: 40,
      },
    },
  }),
)

const poweredByContainer = css(
  {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#fff',
    width: '100%',
    height: 96,
    textAlign: 'center',
    position: 'fixed',
    bottom: 0,
    zIndex: 2,
    img: {
      width: 64,
      height: 64,
    },
  },
  createSelfCheckinResponsiveStyle({ landscape13: { height: 112, img: { width: 72, height: 72 } } }),
)

const hiddenTapZoneStyle = css({
  position: 'fixed',
  bottom: 0,
  left: 0,
  width: 80,
  height: 80,
  zIndex: 3,
})
